/**
 * GeoLocation Component
 * Leaflet map component for capturing multiple geometries (polygons, polylines, markers)
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { useWmsUdaScope } from './common/WmsUdaScope';
import { fetchUdaBoundary, drawUdaBoundary } from '../lib/udaBoundary';
import { udaMapView } from '../lib/udaGeo';
import { TILE_URLS } from '@/config/mapTiles';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});
L.Marker.prototype.options.icon = DefaultIcon;

export interface GeoData {
  markers: Array<{ lat: number; lng: number; label?: string }>;
  polylines: Array<Array<{ lat: number; lng: number }>>;
  polygons: Array<Array<{ lat: number; lng: number }>>;
}

interface GeoLocationProps {
  value?: GeoData | null;
  onChange?: (data: GeoData) => void;
  center?: [number, number];
  zoom?: number;
  height?: string;
  disabled?: boolean;
  /**
   * Which authority's outline to draw underneath, and frame the map on. Defaults to the
   * UDA the WMS screens are scoped to, which is the one the record being drawn belongs
   * to — pass a value only to override that (a work in another UDA, say).
   */
  udaId?: number | null;
  /** Set false for a map that should not carry the authority outline. */
  showUdaBoundary?: boolean;
}

const defaultGeoData: GeoData = {
  markers: [],
  polylines: [],
  polygons: []
};

// Default center: Hyderabad, India
const DEFAULT_CENTER: [number, number] = [17.385, 78.4867];
const DEFAULT_ZOOM = 12;

export default function GeoLocation({
  value,
  onChange,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  height = '400px',
  disabled = false,
  udaId,
  showUdaBoundary = true
}: GeoLocationProps) {
  // The UDA in force on the WMS screens, unless the caller named one.
  const wmsScope = useWmsUdaScope();
  const boundaryUdaId = udaId !== undefined ? udaId : wmsScope.udaId;
  const udaBoundaryRef = useRef<L.GeoJSON | null>(null);
  // Set once the Leaflet instance exists, so the boundary effect below has a map to
  // draw on — refs do not re-render, so the effect cannot key off mapRef alone.
  const [mapReady, setMapReady] = useState(false);
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapLayers, setMapLayers] = useState<{
    markers: L.Marker[];
    polylines: L.Polyline[];
    polygons: L.Polygon[];
  }>({ markers: [], polylines: [], polygons: [] });

  /*
   * The authority outline, from ds_uda_lst_t.extd_bndry_plgn_jn (via the WMS boundaries
   * endpoint) — the same geometry the dashboard's UDA layer draws, so the two maps
   * cannot disagree about where a UDA ends.
   *
   * It replaces the opening centroid as the framing: the map now opens ON the authority
   * rather than near its town centre, which is what tells the operator whether the shape
   * they are about to draw falls inside it. APCRDA and VK-PCPIR hold no polygon, so those
   * keep the centroid view (udaMapView) — a map framed on nothing is worse than a map
   * framed on the right town.
   *
   * Non-interactive, so it never intercepts a click meant for the drawing tools.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let cancelled = false;

    if (udaBoundaryRef.current) {
      map.removeLayer(udaBoundaryRef.current);
      udaBoundaryRef.current = null;
    }
    if (!showUdaBoundary || boundaryUdaId == null) return;

    fetchUdaBoundary(boundaryUdaId)
      .then((feature) => {
        if (cancelled || !mapRef.current) return;
        if (!feature) {
          // No polygon for this authority — open on its town instead of nowhere.
          const view = udaMapView(boundaryUdaId);
          mapRef.current.setView(view.center, view.zoom);
          return;
        }
        // Only frame on the boundary when the operator has nothing of their own on the
        // map yet; once there is a drawn shape, the fit below (or their own edit) owns
        // the view and yanking it out to the whole UDA would lose their work visually.
        const hasOwnGeometry = !!(value && (value.markers?.length || value.polygons?.length || value.polylines?.length));
        udaBoundaryRef.current = drawUdaBoundary(L, mapRef.current, feature, { fit: !hasOwnGeometry });
      })
      .catch(() => { /* fetchUdaBoundary already warns; the map stays usable without it */ });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundaryUdaId, showUdaBoundary, mapReady]);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: center,
      zoom: zoom,
      zoomControl: true
    });

    // Add OpenStreetMap tile layer
    L.tileLayer(TILE_URLS.osm, {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(map);

    // Initialize Geoman controls if not disabled
    if (!disabled) {
      map.pm.addControls({
        position: 'topleft',
        drawCircle: false,
        drawCircleMarker: false,
        drawRectangle: false,
        drawText: false,
        rotateMode: false,
        cutPolygon: false,
        dragMode: true,
        editMode: true,
        removalMode: true
      });

      // Style for drawn items - use circle markers for vertices
      map.pm.setGlobalOptions({
        pathOptions: {
          color: '#3b82f6',
          fillColor: '#3b82f6',
          fillOpacity: 0.2,
          weight: 3
        },
        markerStyle: {
          icon: DefaultIcon
        },
        templineStyle: {
          color: '#3b82f6',
          weight: 2,
          dashArray: '5, 5'
        },
        hintlineStyle: {
          color: '#3b82f6',
          weight: 2,
          dashArray: '5, 5'
        },
        snappable: true,
        snapDistance: 15,
        allowSelfIntersection: false,
        finishOn: 'dblclick'
      });

      // Add CSS to style vertex markers as small circles
      const style = document.createElement('style');
      style.id = 'geolocation-vertex-styles';
      style.textContent = `
        .leaflet-pm-icon-polygon-vertex,
        .leaflet-pm-icon-line-vertex,
        .marker-icon-middle,
        .leaflet-marker-icon.leaflet-pm-draggable:not(.leaflet-marker-icon[src]) {
          width: 12px !important;
          height: 12px !important;
          margin-left: -6px !important;
          margin-top: -6px !important;
          border-radius: 50% !important;
          background: #ffffff !important;
          border: 2px solid #3b82f6 !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3) !important;
        }
        .marker-icon-middle {
          width: 8px !important;
          height: 8px !important;
          margin-left: -4px !important;
          margin-top: -4px !important;
          background: #93c5fd !important;
          border: 2px solid #3b82f6 !important;
        }
        .leaflet-pm-icon-marker-active {
          width: 14px !important;
          height: 14px !important;
          margin-left: -7px !important;
          margin-top: -7px !important;
        }
      `;
      // Only add if not already present
      if (!document.getElementById('geolocation-vertex-styles')) {
        document.head.appendChild(style);
      }
    }

    mapRef.current = map;
    setMapReady(true);

    return () => {
      map.remove();
      mapRef.current = null;
      // Clean up style element
      const styleEl = document.getElementById('geolocation-vertex-styles');
      if (styleEl) {
        styleEl.remove();
      }
    };
  }, []);

  // Extract geo data from map layers
  const extractGeoData = useCallback((): GeoData => {
    if (!mapRef.current) return defaultGeoData;

    const markers: GeoData['markers'] = [];
    const polylines: GeoData['polylines'] = [];
    const polygons: GeoData['polygons'] = [];

    mapRef.current.eachLayer((layer) => {
      if (layer instanceof L.Marker && !(layer instanceof L.CircleMarker)) {
        const latLng = layer.getLatLng();
        markers.push({ lat: latLng.lat, lng: latLng.lng });
      } else if (layer instanceof L.Polygon && !(layer instanceof L.Rectangle)) {
        const latLngs = layer.getLatLngs()[0] as L.LatLng[];
        if (Array.isArray(latLngs)) {
          polygons.push(latLngs.map(ll => ({ lat: ll.lat, lng: ll.lng })));
        }
      } else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
        const latLngs = layer.getLatLngs() as L.LatLng[];
        if (Array.isArray(latLngs)) {
          polylines.push(latLngs.map(ll => ({ lat: ll.lat, lng: ll.lng })));
        }
      }
    });

    return { markers, polylines, polygons };
  }, []);

  // Handle map events for drawing
  useEffect(() => {
    if (!mapRef.current || disabled) return;

    const map = mapRef.current;

    const handleChange = () => {
      const geoData = extractGeoData();
      onChange?.(geoData);
    };

    // Listen for Geoman events
    map.on('pm:create', handleChange);
    map.on('pm:remove', handleChange);
    map.on('pm:edit', handleChange);
    map.on('pm:dragend', handleChange);

    return () => {
      map.off('pm:create', handleChange);
      map.off('pm:remove', handleChange);
      map.off('pm:edit', handleChange);
      map.off('pm:dragend', handleChange);
    };
  }, [extractGeoData, onChange, disabled]);

  // Load initial value
  useEffect(() => {
    if (!mapRef.current || !value) return;

    const map = mapRef.current;

    // Clear existing layers (except tile layer)
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline || layer instanceof L.Polygon) {
        map.removeLayer(layer);
      }
    });

    // Add markers
    value.markers?.forEach((marker) => {
      const m = L.marker([marker.lat, marker.lng], {
        icon: DefaultIcon,
        pmIgnore: disabled
      }).addTo(map);
      if (marker.label) {
        m.bindPopup(marker.label);
      }
    });

    // Add polylines
    value.polylines?.forEach((polyline) => {
      L.polyline(
        polyline.map(p => [p.lat, p.lng]),
        { color: '#3b82f6', weight: 3, pmIgnore: disabled }
      ).addTo(map);
    });

    // Add polygons
    value.polygons?.forEach((polygon) => {
      L.polygon(
        polygon.map(p => [p.lat, p.lng]),
        { color: '#3b82f6', fillColor: '#3b82f6', fillOpacity: 0.2, weight: 3, pmIgnore: disabled }
      ).addTo(map);
    });

    // Fit bounds if there's data
    const allCoords: L.LatLngExpression[] = [
      ...(value.markers?.map(m => [m.lat, m.lng] as L.LatLngExpression) || []),
      ...(value.polylines?.flat().map(p => [p.lat, p.lng] as L.LatLngExpression) || []),
      ...(value.polygons?.flat().map(p => [p.lat, p.lng] as L.LatLngExpression) || [])
    ];

    if (allCoords.length > 0) {
      const bounds = L.latLngBounds(allCoords);
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [value, disabled]);

  // Re-centre when the caller's centre resolves after mount — the map is created once,
  // so a centre that arrives later (e.g. the UDA centroid, once auth has loaded) would
  // otherwise never be applied. Skipped once anything is drawn, so this never fights
  // the fitBounds above or yanks the view away from the user's own work.
  const centerLat = center[0];
  const centerLng = center[1];
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const hasGeometry = (value?.markers?.length || 0) + (value?.polylines?.length || 0) + (value?.polygons?.length || 0) > 0;
    if (hasGeometry) return;
    map.setView([centerLat, centerLng], zoom);
  }, [centerLat, centerLng, zoom]);

  // Settings panel handlers
  const handleClearAll = () => {
    if (!mapRef.current) return;

    mapRef.current.eachLayer((layer) => {
      if (layer instanceof L.Marker || layer instanceof L.Polyline || layer instanceof L.Polygon) {
        mapRef.current?.removeLayer(layer);
      }
    });

    onChange?.(defaultGeoData);
    setIsSettingsOpen(false);
  };

  const handleGoToLocation = () => {
    if (!mapRef.current) return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          mapRef.current?.setView([latitude, longitude], 16);
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to get your location');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
    }
    setIsSettingsOpen(false);
  };

  const handleResetView = () => {
    mapRef.current?.setView(center, zoom);
    setIsSettingsOpen(false);
  };

  // Toggle fullscreen
  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    setIsSettingsOpen(false);
    // Invalidate map size after state change
    setTimeout(() => {
      mapRef.current?.invalidateSize();
    }, 100);
  };

  // Get counts for display
  const getCounts = () => {
    const data = value || defaultGeoData;
    return {
      markers: data.markers?.length || 0,
      polylines: data.polylines?.length || 0,
      polygons: data.polygons?.length || 0
    };
  };

  const counts = getCounts();
  const totalCount = counts.markers + counts.polylines + counts.polygons;

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden border border-gray-300 rounded-lg transition-all duration-200 ${
        isFullscreen ? 'fixed inset-0 z-[99999] rounded-none border-0' : ''
      }`}
      style={isFullscreen ? {} : {}}
    >
      {/* Map Container */}
      <div
        ref={mapContainerRef}
        style={{ height: isFullscreen ? '100vh' : height, width: '100%' }}
      />

      {/* Top Right Buttons */}
      <div className={`absolute z-[1000] flex items-center gap-2 ${isFullscreen ? 'top-4 right-4' : 'top-2 right-2'}`}>
        {/* Fullscreen Button */}
        <button
          type="button"
          onClick={handleFullscreen}
          className={`bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors border border-gray-200 ${isFullscreen ? 'p-2.5' : 'p-2'}`}
          title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
        >
          <i className={`${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} text-gray-600 ${isFullscreen ? 'text-xl' : 'text-lg'}`}></i>
        </button>

        {/* Settings Button with Dropdown */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            className={`bg-white rounded-lg shadow-md hover:bg-gray-50 transition-colors border border-gray-200 ${isFullscreen ? 'p-2.5' : 'p-2'}`}
            title="Map Settings"
          >
            <i className={`ri-settings-3-line text-gray-600 ${isFullscreen ? 'text-xl' : 'text-lg'}`}></i>
          </button>

          {/* Settings Dropdown */}
          {isSettingsOpen && (
            <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px]">
              <button
                type="button"
                onClick={handleGoToLocation}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <i className="ri-crosshair-2-line text-blue-500"></i>
                My Location
              </button>
              <button
                type="button"
                onClick={handleResetView}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <i className="ri-refresh-line text-green-500"></i>
                Reset View
              </button>
              {!disabled && (
                <>
                  <hr className="my-1" />
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                  >
                    <i className="ri-delete-bin-line"></i>
                    Clear All
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats Badge */}
      {totalCount > 0 && (
        <div className={`absolute z-[1000] bg-white/90 backdrop-blur-sm rounded-lg shadow-md border border-gray-200 ${isFullscreen ? 'bottom-4 left-4 px-4 py-2' : 'bottom-2 left-2 px-3 py-1.5'}`}>
          <div className={`flex items-center ${isFullscreen ? 'gap-4 text-sm' : 'gap-3 text-xs'}`}>
            {counts.markers > 0 && (
              <span className="flex items-center gap-1 text-blue-600">
                <i className="ri-map-pin-fill"></i> {counts.markers}{isFullscreen && ' Markers'}
              </span>
            )}
            {counts.polylines > 0 && (
              <span className="flex items-center gap-1 text-green-600">
                <i className="ri-route-line"></i> {counts.polylines}{isFullscreen && ' Lines'}
              </span>
            )}
            {counts.polygons > 0 && (
              <span className="flex items-center gap-1 text-purple-600">
                <i className="ri-shape-line"></i> {counts.polygons}{isFullscreen && ' Areas'}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Disabled Overlay */}
      {disabled && (
        <div className="absolute inset-0 bg-gray-100/50 z-[999] pointer-events-none" />
      )}
    </div>
  );
}
